import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import { AbsenceApproval } from '@/api/model'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { PermissionEntityType } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { useAbsenceEntries } from '../hooks/useAbsenceEntries'
import { AbsenceEntriesTable } from './AbsenceEntriesTable'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import {
  getAbsenceStatusLabel
} from '@/pages/service-modules/open-components/absence/all-entries-components/absenceLabels.ts'
import { unwrap } from '@/utils/unwrap.ts'

interface Props {
  module: ServiceModuleModel
  /** Fixed employee of this view (from the URL, `?view=employee&employeeId=…`). */
  employeeId: string
  onBack: () => void
}

const ALL_STATUSES = 'all'

/**
 * Employee absences page (row click in the module overview → here).
 * Like `AbsenceAllEntriesPage`, but the employee is fixed (no employee filter)
 * and **without** a calendar. Only a date-range filter + paginated list for the
 * employee. The employee comes as an id from the URL (shareable / reload-safe) —
 * the name is looked up from the (cached) employee list of the module.
 */
export function AbsenceEmployeeEntriesPage({ module, employeeId, onBack }: Props) {
  const { t } = useTranslation(['service-modules-absence', 'common', 'filters'])
  const { userHasEntityPermission } = usePermissions()
  const [range, setRange] = useState<{ from?: string; to?: string }>({})
  // Approve/reject depends on ServiceModules.Edit of this module (global or object-specific).
  const canEdit = userHasEntityPermission(PermissionEntityType.ServiceModules, 'Edit', module.id)
  const [status, setStatus] = useState<AbsenceApproval | undefined>()

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employee = useMemo(
    () => (unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? []).find((e) => e.id === employeeId),
    [employeesQuery.data, employeeId],
  )

  const entries = useAbsenceEntries(module.id, {
    employeeUserId: employeeId,
    from: range.from,
    to: range.to,
    status,
  })
  const name = `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim() || t('dropdown.employeeFallback', { ns: 'filters' })

  return (
    <PageLayout>
      <PageHeader title={t('absence.employeeTitle', { name })} description={t('absence.employeeDescription')}>
        <PageHeaderActions>
          <Button variant="outline" className="h-9 gap-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterRow>
        <FilterDateRangePicker from={range.from} to={range.to} onChange={setRange} />
        <Select
          value={status ?? ALL_STATUSES}
          onValueChange={(v) => setStatus(v === ALL_STATUSES ? undefined : v as AbsenceApproval)}
        >
          <SelectTrigger className="w-44 h-9!">
            <SelectValue placeholder={t('dropdown.statusPlaceholder', { ns: 'filters' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('dropdown.allStatuses', { ns: 'filters' })}</SelectItem>
            {Object.values(AbsenceApproval).map((s) => (
              <SelectItem key={s} value={s}>
                {getAbsenceStatusLabel(t, s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterRow>

      <AbsenceEntriesTable entries={entries} canEdit={canEdit} />
    </PageLayout>
  )
}
